import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuthContext } from "../../firebase-files/hooks/useAuthContext";
import "swiper/swiper-bundle.css";
import ReactSwitch from "react-switch";
import { useFirestore } from "../../firebase-files/hooks/useFirestore";
import { projectStorage } from "../../firebase-config/config";
import { useCollection } from "../../firebase-files/hooks/useCollection";
import { useDocument } from "../../firebase-files/hooks/useDocument";

const PGTeam = () => {
  const { user } = useAuthContext();
  const isAdmin = user && user.role === "admin";
  const isSuperAdmin = user && user.role === "superAdmin";

  const { documents: dbMediaDocuments } = useCollection("our_team");
  const { addDocument, updateDocument, deleteDocument } =
    useFirestore("our_team");
  const { document: dbAppTypeDocuments } = useDocument("settings", "AppType");

  const teamImagesDocuments =
    dbMediaDocuments
      ?.filter((item) => {
        if (isAdmin || isSuperAdmin) {
          // Admin or SuperAdmin sees all images
          return item.mediaType.toUpperCase() === "IMAGE";
        }
        // Other roles see only active images
        return (
          item.mediaType.toUpperCase() === "IMAGE" && item.status === "active"
        );
      })
      .sort((a, b) => a.Sequence - b.Sequence) || [];

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [pageUrl, setPageUrl] = useState("");
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [editDetails, setEditDetails] = useState({
    id: "",
    mediaUrl: "",
    pageUrl: "",
    status: "inactive",
  });

  // Add new image popup
  const handleAddTeamClick = () => 
    setIsPopupOpen(true);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfirm = async () => {
    setIsUploading(true);
    try {
      const fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}.jpg`;

      const storageRef = projectStorage.ref(`team_images/${fileName}`);
      const uploadTask = await storageRef.putString(selectedImage, "data_url");
      const mediaUrl = await uploadTask.ref.getDownloadURL();

      await addDocument({
        mediaUrl,
        pageUrl,
        mediaType: "IMAGE",
        status: "active",
        createdAt: new Date(),
      });

      alert("Image added successfully!");
    } catch (error) {
      console.error("Error adding image:", error);//Default status
      alert("Failed to add the image. Please try again.");//Optinal: add a timestamp
    }

    setIsUploading(false);//Hide uploading message
    setIsPopupOpen(false);
    setSelectedImage(null);
    setPageUrl("");
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
    setSelectedImage(null);
    setIsEditPopupOpen(false);
    setPageUrl("");
  };

  const handleEditClick = (image) => {
    setEditDetails({
      id: image.id,
      mediaUrl: image.mediaUrl,
      pageUrl: image.pageUrl,
      status: image.status,
    });
    setIsEditPopupOpen(true);
  };

  const handleEditSave = async () => {
    const { id, pageUrl, status } = editDetails;

    try {
      await updateDocument(id, { pageUrl, status });
      alert("Details updated successfully!");
    } catch (error) {
      console.error("Error updating details:", error);
      alert("Failed to update details.");
    }
    setIsEditPopupOpen(false);
  };

  const handleEditDelete = async () => {
    try {
      await deleteDocument(editDetails.id);
      alert("Document deleted successfully!");
    } catch (error) {
      console.error("Error deleting document:", error);
      alert("Failed to delete document.");
    }
    setIsEditPopupOpen(false);
  };

  return (
    <div className="container mt-4">
      <div className="row">
        {teamImagesDocuments.map((teamImage) => (
          <div
            key={teamImage.id}
            className="col-6 col-md-4 col-lg-3 default_template_team_image_wrapper mt-2 mb-2 m-0"
          >
            <Link to={teamImage.pageUrl}>
              <img
                src={teamImage.mediaUrl}
                alt="Offer"
                className="default_template_team_offer_img_carousel"
              />
            </Link>

            {/* Edit icon visible only to admin or super admin in PRO app type */}
            {(dbAppTypeDocuments?.apptype === "pro" &&
              (isSuperAdmin || isAdmin)) ||
            (dbAppTypeDocuments?.apptype === "lite" && isSuperAdmin) ? (
              <span
                className="default_template_team_edit_icon material-symbols-outlined"
                onClick={() => handleEditClick(teamImage)}
              >
                edit
              </span>
            ) : null}
          </div>
        ))}
      </div>

      <br />
      <div className="d-flex justify-content-center">
        {/* Add button allowed for pro(superAdmin, admin) and lite(superAdmin) */}
        {(dbAppTypeDocuments?.apptype === "pro" &&
          (isSuperAdmin || isAdmin)) ||
        (dbAppTypeDocuments?.apptype === "lite" && isSuperAdmin) ? (
          <button className="btn_fill" onClick={handleAddTeamClick}>
            Add Team
          </button>
        ) : null}
      </div>
      <br />

      {/* Popup for adding new team member */}
      {isPopupOpen && (
        <div className="default_template_team_popup_overlay">
          <div className="default_template_team_popup">
            <h3>Select an Image</h3>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="default_template_team_file_input"
            />
            {selectedImage && (
              <div>
                <img
                  src={selectedImage}
                  alt="Selected"
                  className="default_template_team_selected_img"
                />
                <input
                  type="text"
                  placeholder="Enter page URL"
                  value={pageUrl}
                  onChange={(e) => setPageUrl(e.target.value)}
                  className="default_template_team_page_url_input"
                />
                <div className="d-flex justify-content-between">
                  {isUploading ? (
                    <span>Uploading...</span>
                  ) : (
                    <>
                      <button onClick={handleCancel} className="btn_border">
                        Cancel
                      </button>
                      <button onClick={handleConfirm} className="btn_fill">
                        Confirm
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            {!selectedImage && (
              <button onClick={handleCancel} className="btn_border">
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Popup for editing/deleting team member */}
      {isEditPopupOpen && (
        <div className="default_template_team_popup_overlay">
          <div className="default_template_team_popup_content">
            <img src={editDetails.mediaUrl} alt="Edit" />
            <input
              type="text"
              value={editDetails.pageUrl}
              onChange={(e) =>
                setEditDetails({ ...editDetails, pageUrl: e.target.value })
              }
            />
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <p className="active-inactive-text" style={{ color: "red" }}>
                  Inactive
                </p>
                <ReactSwitch
                  checked={editDetails.status === "active"}
                  onChange={(checked) =>
                    setEditDetails({
                      ...editDetails,
                      status: checked ? "active" : "inactive",
                    })
                  }
                  onColor="#149AA2"
                  offColor="#F05B83"
                  className="products-status-switch"
                />
                <p className="active-inactive-text" style={{ color: "red" }}>
                  Active
                </p>
              </div>

              {/* delete visible only to superAdmin (pro/lite) */}
              {((dbAppTypeDocuments?.apptype === "pro" && isSuperAdmin) ||
                (dbAppTypeDocuments?.apptype === "lite" && isSuperAdmin)) && (
                <span
                  className="material-symbols-outlined"
                  onClick={handleEditDelete}
                  style={{ cursor: "pointer", zIndex: 10, position: "relative" }}
                   
                >
                  delete
                </span>
              )}
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <button onClick={handleCancel} className="btn_border">
                Cancel
              </button>
              <button onClick={handleEditSave} className="btn_fill">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PGTeam;

